import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider } from './contexts/AuthContext';
import Layout from './components/Layout/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Employees from './pages/Employees/Employees';
import EmployeeLeaveManagement from './pages/Employees/EmployeeLeaveManagement';
import Admin from './pages/Admin/Admin';
import Leave from './pages/Leave/Leave';
import Payroll from './pages/Payroll/Payroll';
import Recruitment from './pages/Recruitment/Recruitment';
import Performance from './pages/Performance/Performance';
import Onboarding from './pages/Onboarding/Onboarding';
import Offboarding from './pages/Offboarding/Offboarding';
import Career from './pages/Career/Career';
import Goals from './pages/Goals/Goals';
import Milestones from './pages/Milestones/Milestones';
import SalaryReview from './pages/SalaryReview/SalaryReview';
import HrInbox from './pages/HrInbox/HrInbox';
import LeaveBalanceReport from './pages/LeaveBalanceReport/LeaveBalanceReport';
import AttendanceReport from './pages/AttendanceReport/AttendanceReport';
import ProbationTracker from './pages/ProbationTracker/ProbationTracker';
import TimeAttendance from './pages/TimeAttendance/TimeAttendance';
import Overtime from './pages/Overtime/Overtime';
import ExitInterviews from './pages/ExitInterviews/ExitInterviews';
import Policies from './pages/Policies/Policies';
import Benefits from './pages/Benefits/Benefits';
import PayslipViewer from './pages/PayslipViewer/PayslipViewer';
import Headcount from './pages/Headcount/Headcount';
import Disciplinary from './pages/Disciplinary/Disciplinary';
import EmployeeDirectory from './pages/EmployeeDirectory/EmployeeDirectory';
import Roster from './pages/Roster/Roster';
import AttendanceCorrection from './pages/AttendanceCorrection/AttendanceCorrection';
import LeaveEncashment from './pages/LeaveEncashment/LeaveEncashment';
import ShiftSwap from './pages/ShiftSwap/ShiftSwap';
import ExpenseReports from './pages/ExpenseReports/ExpenseReports';
import LeaveCarryover from './pages/LeaveCarryover/LeaveCarryover';
import PayrollVariance from './pages/PayrollVariance/PayrollVariance';
import LabourCost from './pages/LabourCost/LabourCost';
import EmployeeNotes from './pages/EmployeeNotes/EmployeeNotes';
import SuccessionPipeline from './pages/SuccessionPipeline/SuccessionPipeline';
import CompanySettings from './pages/CompanySettings/CompanySettings';
import ToilBank from './pages/ToilBank/ToilBank';
import Delegation from './pages/Delegation/Delegation';
import OrgChanges from './pages/OrgChanges/OrgChanges';
import SalaryHistory from './pages/SalaryHistory/SalaryHistory';
import ManagerDashboard from './pages/ManagerDashboard/ManagerDashboard';
import ComplianceDocs from './pages/ComplianceDocs/ComplianceDocs';
import JdTemplates from './pages/JdTemplates/JdTemplates';
import NoticePeriod from './pages/NoticePeriod/NoticePeriod';
import LetterTemplates from './pages/LetterTemplates/LetterTemplates';
import PayEquity from './pages/PayEquity/PayEquity';
import PreBoarding from './pages/PreBoarding/PreBoarding';
import PreBoardingPortal from './pages/PreBoarding/PreBoardingPortal';
import ManpowerReport from './pages/ManpowerReport/ManpowerReport';
import Absence from './pages/Absence/Absence';
import TurnoverRisk from './pages/TurnoverRisk/TurnoverRisk';
import OrgRisk from './pages/OrgRisk/OrgRisk';
import ExecutiveDashboard from './pages/ExecutiveDashboard/ExecutiveDashboard';
import TrainingRoi from './pages/TrainingRoi/TrainingRoi';
import LeavePolicyAdmin from './pages/LeavePolicyAdmin/LeavePolicyAdmin';
import HRCalendar from './pages/HRCalendar/HRCalendar';
import EmergencyContacts from './pages/EmergencyContacts/EmergencyContacts';
import PublicHolidays from './pages/PublicHolidays/PublicHolidays';
import WFH from './pages/WFH/WFH';
import WorkforceAnalytics from './pages/WorkforceAnalytics/WorkforceAnalytics';
import Assets from './pages/Assets/Assets';
import Contracts from './pages/Contracts/Contracts';
import Expenses from './pages/Expenses/Expenses';
import OrgChart from './pages/OrgChart/OrgChart';
import Reports from './pages/Reports/Reports';
import Documents from './pages/Documents/Documents';
import BusinessTrips from './pages/BusinessTrips/BusinessTrips';

import MyProfile from './pages/Profile/MyProfile';
import EmployeeProfile from './pages/Employees/EmployeeProfile';
import ResetPassword from './pages/Auth/ResetPassword';
import ForgotPassword from './pages/Auth/ForgotPassword';
import AcceptInvite from './pages/AcceptInvite';
import FirstLogin from './pages/FirstLogin';
import ProtectedRoute from './components/ProtectedRoute';
import ForceChangePassword from './pages/ForceChangePassword';
import IR56B from './pages/IR56B/IR56B';
import BankFile from './pages/BankFile/BankFile';
import Calibration from './pages/Calibration/Calibration';
import PIP from './pages/PIP/PIP';
// Chain B — Analytics & Operations modules
import CustomFields from './pages/CustomFields/CustomFields';
import LeaveAdjustments from './pages/LeaveAdjustments/LeaveAdjustments';
import DataQuality from './pages/DataQuality/DataQuality';
import RecruitmentAnalytics from './pages/RecruitmentAnalytics/RecruitmentAnalytics';
import PayrollJournal from './pages/PayrollJournal/PayrollJournal';
import PositionRegistry from './pages/PositionRegistry/PositionRegistry';
import EmployeeTimelinePage from './pages/EmployeeTimeline/EmployeeTimelinePage';
import HrMetrics from './pages/HrMetrics/HrMetrics';
import ApprovalsHub from './pages/ApprovalsHub/ApprovalsHub';
import EmployeeDashboard from './pages/EmployeeDashboard/EmployeeDashboard';
import CostPerEmployee from './pages/CostPerEmployee/CostPerEmployee';
import LeavePatterns from './pages/LeavePatterns/LeavePatterns';
import SpanOfControl from './pages/SpanOfControl/SpanOfControl';
import EngagementScore from './pages/EngagementScore/EngagementScore';
import PayrollAnomalies from './pages/PayrollAnomalies/PayrollAnomalies';
// Chain C — Performance & Development modules
import Compensation from './pages/Compensation/Compensation';
import IDP from './pages/IDP/IDP';
import Shifts from './pages/Shifts/Shifts';
import Succession from './pages/Succession/Succession';

// ---------------------------------------------------------------------------
// "Paper & Ink" design language (see BRAND.md) mapped onto the MUI v7 theme.
// Tokens: paper #F7F6F2 / white #FFFFFF / ink #16223B / gold #C08A2E /
// hairline #E5E2DA. No dark mode. No shadows — depth is borders + tints only.
// ---------------------------------------------------------------------------
const PAPER = '#F7F6F2';
const WHITE = '#FFFFFF';
const INK = '#16223B';
const GOLD = '#C08A2E';
const HAIRLINE = '#E5E2DA';
const PAID_TINT = '#F3E6CD'; // "done"/active/selected background tint (pale gold)

// ink opacity steps (BRAND §2 text hierarchy)
const inkAlpha = (a: number) => `rgba(22,34,59,${a})`;

const DISPLAY_FONT = '"Space Grotesk", "IBM Plex Sans", "Helvetica", "Arial", sans-serif';
const BODY_FONT = '"IBM Plex Sans", "Helvetica", "Arial", sans-serif';

// small-caps / wide-tracking chrome label recipe (BRAND §3 section label)
const overlineLabel = {
  fontFamily: BODY_FONT,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.2em',
  fontVariant: 'small-caps',
  fontWeight: 600,
  color: inkAlpha(0.4),
};

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: GOLD,
      light: '#CFA255',
      dark: '#9E6F22',
      contrastText: WHITE,
    },
    secondary: {
      // muted approved-green, kept as a secondary accent (was a saturated green)
      main: '#2E7D5B',
      light: '#4F9B79',
      dark: '#22684A',
      contrastText: WHITE,
    },
    background: {
      default: PAPER,
      paper: WHITE,
    },
    text: {
      primary: INK,
      secondary: inkAlpha(0.6),
      disabled: inkAlpha(0.4),
    },
    divider: HAIRLINE,
    success: { main: '#2E7D5B', contrastText: WHITE },
    warning: { main: GOLD, contrastText: WHITE },
    error: { main: '#A33B2E', contrastText: WHITE },
    info: { main: GOLD, contrastText: WHITE },
  },
  typography: {
    fontFamily: BODY_FONT,
    // Internal tools run one notch smaller than consumer defaults (BRAND §3).
    // Base is dropped to 13px and the body variants set explicitly so text is
    // dense like a ledger. Numbers use tabular figures so columns align.
    fontSize: 13,
    body1: { fontSize: '0.8125rem', fontVariantNumeric: 'tabular-nums' },
    body2: { fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums' },
    button: { fontSize: '0.8125rem', textTransform: 'none', fontWeight: 600 },
    // headings + card titles use Space Grotesk, semibold, tight tracking
    h1: { fontFamily: DISPLAY_FONT, fontWeight: 600, letterSpacing: '-0.02em' },
    h2: { fontFamily: DISPLAY_FONT, fontWeight: 600, letterSpacing: '-0.02em' },
    h3: { fontFamily: DISPLAY_FONT, fontWeight: 600, letterSpacing: '-0.02em' },
    h4: { fontFamily: DISPLAY_FONT, fontWeight: 600, letterSpacing: '-0.02em' },
    h5: { fontFamily: DISPLAY_FONT, fontWeight: 600, letterSpacing: '-0.02em' },
    h6: { fontFamily: DISPLAY_FONT, fontWeight: 600, letterSpacing: '-0.02em' },
    subtitle1: { fontFamily: DISPLAY_FONT, fontWeight: 600, letterSpacing: '-0.01em' },
    overline: overlineLabel,
  },
  shape: { borderRadius: 6 }, // controls = rounded-md; cards/tables override to 8–12
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: PAPER,
          color: INK,
          fontVariantNumeric: 'tabular-nums',
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true, size: 'small' },
      styleOverrides: {
        // Compact by default (BRAND §6: px-3 py-1.5). Density comes from the theme
        // so page markup stays untouched.
        root: { textTransform: 'none', fontWeight: 600, borderRadius: 6, boxShadow: 'none', padding: '4px 12px', fontSize: '0.8125rem' },
        contained: {
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
        },
        containedPrimary: {
          backgroundColor: GOLD,
          color: WHITE,
          '&:hover': { backgroundColor: '#B07E29', boxShadow: 'none' },
        },
        outlinedPrimary: {
          borderColor: GOLD,
          color: GOLD,
          backgroundColor: WHITE,
          '&:hover': { borderColor: GOLD, backgroundColor: 'rgba(192,138,46,0.10)' },
        },
        textPrimary: {
          color: GOLD,
          '&:hover': { backgroundColor: 'rgba(192,138,46,0.08)' },
        },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          boxShadow: 'none',
        },
        // outlined + elevated surfaces both get a hairline, no shadow
        rounded: { borderRadius: 8 }, // rounded-lg (BRAND §5)
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: 8, // rounded-lg (BRAND §5)
          boxShadow: 'none',
          border: `1px solid ${HAIRLINE}`,
          backgroundImage: 'none',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          borderRadius: 9999,
          fontVariant: 'small-caps',
          // neutral (default) filled chip = soft hairline tint + muted ink
          '&.MuiChip-filledDefault': { backgroundColor: 'rgba(229,226,218,0.5)', color: inkAlpha(0.6) },
        },
        // Tinted status pills — 12% tint bg + status-color text (BRAND §6 state pill).
        // Semantic colors resolve to gold / green / red via palette; use soft fills.
        colorPrimary: { backgroundColor: 'rgba(192,138,46,0.12)', color: GOLD },
        colorWarning: { backgroundColor: 'rgba(192,138,46,0.12)', color: GOLD },
        colorSuccess: { backgroundColor: 'rgba(46,125,91,0.12)', color: '#2E7D5B' },
        colorError: { backgroundColor: 'rgba(163,59,46,0.12)', color: '#A33B2E' },
        colorInfo: { backgroundColor: 'rgba(192,138,46,0.12)', color: GOLD },
      },
    },
    MuiTable: {
      // Dense rows by default — a ledger, not a consumer table (BRAND §6).
      defaultProps: { size: 'small' },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${inkAlpha(0.06)}`,
          fontVariantNumeric: 'tabular-nums',
        },
        // tight row rhythm (~py-2)
        sizeSmall: { padding: '5px 14px' },
        head: {
          backgroundColor: 'transparent',
          fontWeight: 600,
          color: inkAlpha(0.4),
          fontSize: '0.6875rem',
          textTransform: 'uppercase',
          letterSpacing: '0.14em',
          fontVariant: 'small-caps',
          borderBottom: `1px solid ${HAIRLINE}`,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: 'transparent',
            fontWeight: 600,
            color: inkAlpha(0.4),
            fontSize: '0.6875rem',
            textTransform: 'uppercase',
            letterSpacing: '0.14em',
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          // hover = pale gold tint, never gray (BRAND §6 tables)
          '&.MuiTableRow-hover:hover': { backgroundColor: 'rgba(243,230,205,0.30)' },
          '&.Mui-selected': { backgroundColor: 'rgba(243,230,205,0.5)' },
          '&.Mui-selected:hover': { backgroundColor: 'rgba(243,230,205,0.6)' },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: { borderBottom: `1px solid ${HAIRLINE}`, minHeight: 40 },
        indicator: { backgroundColor: GOLD, height: 2 },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontVariant: 'small-caps',
          letterSpacing: '0.02em',
          minHeight: 40,
          color: inkAlpha(0.5),
          '&.Mui-selected': { color: INK },
        },
      },
    },
    MuiTextField: { defaultProps: { size: 'small' } },
    MuiSelect: { defaultProps: { size: 'small' } },
    MuiFormControl: { defaultProps: { size: 'small' } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: HAIRLINE },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: inkAlpha(0.3) },
          // focus = border turns gold, no ring
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: GOLD, borderWidth: 1 },
        },
        input: {
          '&::placeholder': { color: inkAlpha(0.4), opacity: 1 },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: { color: inkAlpha(0.6), '&.Mui-focused': { color: GOLD } },
      },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0, color: 'default' },
      styleOverrides: {
        root: {
          backgroundColor: WHITE,
          color: INK,
          boxShadow: 'none',
          borderBottom: `1px solid ${HAIRLINE}`,
          backgroundImage: 'none',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: 'rgba(255,255,255,0.6)',
          borderRight: `1px solid ${HAIRLINE}`,
          boxShadow: 'none',
          backgroundImage: 'none',
        },
      },
    },
    MuiDivider: {
      styleOverrides: { root: { borderColor: HAIRLINE } },
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/force-change-password" element={<ProtectedRoute><ForceChangePassword /></ProtectedRoute>} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/accept-invite/:token" element={<AcceptInvite />} />
            <Route path="/pre-boarding/portal/:token" element={<PreBoardingPortal />} />
            <Route path="/first-login" element={<FirstLogin />} />
            <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
              <Route index element={<Dashboard />} />
              <Route path="employees" element={<Employees />} />
              <Route path="employees/:id" element={<EmployeeProfile />} />
              <Route path="employees/leave" element={<EmployeeLeaveManagement />} />
              <Route path="admin" element={<Admin />} />
              <Route path="leave" element={<Leave />} />
              <Route path="payroll" element={<Payroll />} />
              <Route path="recruitment" element={<Recruitment />} />
              <Route path="performance" element={<Performance />} />
              <Route path="onboarding" element={<Onboarding />} />
              <Route path="offboarding" element={<Offboarding />} />
              <Route path="career" element={<Career />} />
              <Route path="goals" element={<Goals />} />
              <Route path="milestones" element={<Milestones />} />
              <Route path="salary-review" element={<SalaryReview />} />
              <Route path="hr-inbox" element={<HrInbox />} />
              <Route path="leave-balance-report" element={<LeaveBalanceReport />} />
              <Route path="attendance-report" element={<AttendanceReport />} />
              <Route path="probation-tracker" element={<ProbationTracker />} />
              <Route path="attendance" element={<TimeAttendance />} />
              <Route path="overtime" element={<Overtime />} />
              <Route path="exit-interviews" element={<ExitInterviews />} />
              <Route path="policies" element={<Policies />} />
              <Route path="benefits" element={<Benefits />} />
              <Route path="payslips" element={<PayslipViewer />} />
              <Route path="headcount" element={<Headcount />} />
              <Route path="disciplinary" element={<Disciplinary />} />
              <Route path="directory" element={<EmployeeDirectory />} />
              <Route path="roster" element={<Roster />} />
              <Route path="attendance-corrections" element={<AttendanceCorrection />} />
              <Route path="leave-encashment" element={<LeaveEncashment />} />
              <Route path="shift-swaps" element={<ShiftSwap />} />
              <Route path="expense-reports" element={<ExpenseReports />} />
              <Route path="leave-carryover" element={<LeaveCarryover />} />
              <Route path="payroll-variance" element={<PayrollVariance />} />
              <Route path="labour-cost" element={<LabourCost />} />
              <Route path="employee-notes" element={<EmployeeNotes />} />
              <Route path="succession-pipeline" element={<SuccessionPipeline />} />
              <Route path="company-settings" element={<CompanySettings />} />
              <Route path="toil" element={<ToilBank />} />
              <Route path="delegation" element={<Delegation />} />
              <Route path="org-changes" element={<OrgChanges />} />
              <Route path="salary-history" element={<SalaryHistory />} />
              <Route path="manager-dashboard" element={<ManagerDashboard />} />
              <Route path="compliance-docs" element={<ComplianceDocs />} />
              <Route path="jd-templates" element={<JdTemplates />} />
              <Route path="notice-period" element={<NoticePeriod />} />
              <Route path="letter-templates" element={<LetterTemplates />} />
              <Route path="pay-equity" element={<PayEquity />} />
              <Route path="pre-boarding" element={<PreBoarding />} />
              <Route path="manpower-report" element={<ManpowerReport />} />
              <Route path="absence" element={<Absence />} />
              <Route path="turnover-risk" element={<TurnoverRisk />} />
              <Route path="org-risk" element={<OrgRisk />} />
              <Route path="executive-dashboard" element={<ExecutiveDashboard />} />
              <Route path="training-roi" element={<TrainingRoi />} />
              <Route path="leave-policy-admin" element={<LeavePolicyAdmin />} />
              <Route path="hr-calendar" element={<HRCalendar />} />
              <Route path="emergency-contacts" element={<EmergencyContacts />} />
              <Route path="public-holidays" element={<PublicHolidays />} />
              <Route path="wfh" element={<WFH />} />
              <Route path="workforce-analytics" element={<WorkforceAnalytics />} />
              <Route path="assets" element={<Assets />} />
              <Route path="contracts" element={<Contracts />} />
              <Route path="expenses" element={<Expenses />} />
              <Route path="ir56b" element={<IR56B />} />
              <Route path="bank-file" element={<BankFile />} />
              <Route path="calibration" element={<Calibration />} />
              <Route path="pip" element={<PIP />} />
              <Route path="org-chart" element={<OrgChart />} />
              <Route path="reports" element={<Reports />} />
              <Route path="documents" element={<Documents />} />
              <Route path="business-trips" element={<BusinessTrips />} />

              <Route path="profile" element={<MyProfile />} />
              {/* Chain B routes */}
              <Route path="custom-fields" element={<CustomFields />} />
              <Route path="leave-adjustments" element={<LeaveAdjustments />} />
              <Route path="data-quality" element={<DataQuality />} />
              <Route path="recruitment-analytics" element={<RecruitmentAnalytics />} />
              <Route path="payroll-journal" element={<PayrollJournal />} />
              <Route path="position-registry" element={<PositionRegistry />} />
              <Route path="employee-timeline" element={<EmployeeTimelinePage />} />
              <Route path="hr-metrics" element={<HrMetrics />} />
              <Route path="approvals-hub" element={<ApprovalsHub />} />
              <Route path="my-dashboard" element={<EmployeeDashboard />} />
              <Route path="cost-per-employee" element={<CostPerEmployee />} />
              <Route path="leave-patterns" element={<LeavePatterns />} />
              <Route path="span-of-control" element={<SpanOfControl />} />
              <Route path="engagement-score" element={<EngagementScore />} />
              <Route path="payroll-anomalies" element={<PayrollAnomalies />} />
              {/* Chain C routes */}
              <Route path="compensation" element={<Compensation />} />
              <Route path="idp" element={<IDP />} />
              <Route path="shifts" element={<Shifts />} />
              <Route path="succession" element={<Succession />} />
            </Route>
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
